import { IRowHookOutputInternal } from 'dromo-uploader-js'
import { IRowHookInput, IRowToAdd } from 'dromo-uploader-js/dist/interfaces'
import {
  ProposedSolarLeaderboardSite,
  ProposedSiteAction,
  SiteListType,
  ExistingSolarLeaderboardSiteRecordMatch,
  ExistingRecordMatch,
} from '@black-bear-energy/black-bear-energy-common'
import { setSiteAction } from '../DromoUploader/setSiteAction'
import { AxiosInstance } from 'axios'
import { parseBoolean } from '../../BootstrapTable/Filter/Boolean'
import { highPriorityFields } from '../../SolarLeaderboardUpload/FieldSchema'

export async function generateSiteActions({
  records,
  isUpdate,
  reitId,
  api,
}: {
  records: IRowHookInput[]
  isUpdate: boolean
  reitId: number
  api: AxiosInstance
}): Promise<{
  updatedRows: IRowHookOutputInternal[]
  missingDataRows: (IRowToAdd & { rowId: string })[]
  siteActionResponse: ProposedSiteAction[]
}> {
  if (records.length === 0) {
    return { updatedRows: [], missingDataRows: [], siteActionResponse: [] }
  }

  const recordIdToIndexMap: Map<string, number> = new Map<string, number>()

  const proposedSites: ProposedSolarLeaderboardSite[] = records.map(
    (record) => {
      recordIdToIndexMap.set(record.rowId, record.index)
      return {
        id: record.rowId,
        systemSize: record.row.systemSize?.value
          ? parseFloat(String(record.row.systemSize.value))
          : undefined,
        name: record.row.name.value as string,
        streetAddress: record.row.address.value as string | undefined,
        latitude: record.row.latitude.value
          ? parseFloat(String(record.row.latitude.value))
          : undefined,
        longitude: record.row.longitude.value
          ? parseFloat(String(record.row.longitude.value))
          : undefined,
        sold: record.row.sold?.value
          ? parseBoolean(String(record.row.sold.value))
          : undefined,
      } satisfies ProposedSolarLeaderboardSite
    }
  )

  const siteActionResponse = (
    await api.post<ProposedSiteAction<{ [key: string]: string | number }>[]>(
      '/solar-leaderboard-sites/generate-actions',
      {
        sites: proposedSites,
        reitId,
      }
    )
  ).data

  const updatedRows: IRowHookOutputInternal[] = []
  const missingDataRows: (IRowToAdd & { rowId: string })[] = []

  for (const siteAction of siteActionResponse) {
    const matchingRecord = records.find(
      (r) => r.rowId === siteAction.id
    ) as IRowHookInput

    // If this is the initial upload and this record is missing high-priority information,
    // move this record to the top of the table
    if (!isUpdate && isMissingHighPriorityData(matchingRecord)) {
      missingDataRows.push(generateMissingDataRow(matchingRecord, siteAction))
    } else {
      // apply to the existing row
      updatedRows.push(
        setSiteAction({
          siteListType: SiteListType.Sold, // allow 'Mark as Sold' as default site action
          siteAction,
          record: matchingRecord,
          isUpdate,
          getMatchingDBRecordDecription,
        })
      )
    }
  }
  return {
    updatedRows,
    missingDataRows,
    siteActionResponse: siteActionResponse as ProposedSiteAction[],
  }
}

function getMatchingDBRecordDecription(match: ExistingRecordMatch): string {
  const castMatch = match as ExistingSolarLeaderboardSiteRecordMatch
  return `Name: "${castMatch.name}"<br>Address: ${castMatch.fullAddress}<br>System Size: ${castMatch.systemSize}`
}

function isMissingHighPriorityData(record: IRowHookInput): boolean {
  for (const field of highPriorityFields) {
    if (record.row[field] && !record.row[field].value) {
      return true
    }
  }

  return false
}

function generateMissingDataRow(
  record: IRowHookInput,
  siteAction: ProposedSiteAction<{ [key: string]: string | number }>
): IRowToAdd & { rowId: string } {
  const newRow = setSiteAction({
    siteListType: SiteListType.Sold, // allow 'Mark as Sold' as default site action
    siteAction,
    record,
    isUpdate: false,
    getMatchingDBRecordDecription,
  })
  // add to top of table
  newRow.index = 0

  return newRow as IRowToAdd & { rowId: string }
}
